import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, permissionsApi } from '../../api/endpoints';
import {
  UserDto,
  AppModule,
  PermissionLevel,
  AppModuleLabels,
  PermissionLevelLabels,
} from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';

const MODULES: AppModule[] = [
  'Dashboard',
  'PurchaseOrders',
  'Shipments',
  'Invoices',
  'Products',
  'Inventory',
  'Users',
  'Sync',
];

const PERMISSION_LEVELS: PermissionLevel[] = ['None', 'Read', 'Write'];

type UserPermissionsMap = Record<string, Record<string, string>>;

const PermissionsPage: React.FC = () => {
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<UserPermissionsMap>({});
  const [originalPermissions, setOriginalPermissions] = useState<UserPermissionsMap>({});
  const [savingUserId, setSavingUserId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const usersRes = await usersApi.list();
      const userList: UserDto[] = usersRes.data;
      setUsers(userList);

      const permResults = await Promise.all(
        userList.map((user) => permissionsApi.getForUser(user.id))
      );

      const permsMap: UserPermissionsMap = {};
      userList.forEach((user, index) => {
        const raw = permResults[index].data;
        const userPerms: Record<string, string> = {};
        if (Array.isArray(raw)) {
          raw.forEach((p: { module: string; level: string }) => {
            userPerms[p.module] = p.level;
          });
        } else if (raw && typeof raw === 'object') {
          Object.assign(userPerms, raw);
        }
        MODULES.forEach((mod) => {
          if (!userPerms[mod]) {
            userPerms[mod] = 'None';
          }
        });
        permsMap[user.id] = userPerms;
      });

      setPermissions(permsMap);
      setOriginalPermissions(JSON.parse(JSON.stringify(permsMap)));
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Blad pobierania danych uprawnien'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePermissionChange = useCallback(
    (userId: string, module: string, level: string) => {
      setPermissions((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          [module]: level,
        },
      }));
    },
    []
  );

  const isRowDirty = useCallback(
    (userId: string): boolean => {
      const current = permissions[userId];
      const original = originalPermissions[userId];
      if (!current || !original) return false;
      return MODULES.some((mod) => current[mod] !== original[mod]);
    },
    [permissions, originalPermissions]
  );

  const handleSave = useCallback(
    async (userId: string) => {
      const userPerms = permissions[userId];
      if (!userPerms) return;

      setSavingUserId(userId);
      try {
        const payload: Record<string, string> = {};
        MODULES.forEach((mod) => {
          payload[mod] = userPerms[mod] || 'None';
        });
        await permissionsApi.setForUser(userId, payload);
        toast.success('Uprawnienia zapisane');
        setOriginalPermissions((prev) => ({
          ...prev,
          [userId]: { ...userPerms },
        }));
      } catch (error: any) {
        toast.error(
          error.response?.data?.message || 'Blad zapisu uprawnien'
        );
      } finally {
        setSavingUserId(null);
      }
    },
    [permissions]
  );

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Uprawnienia</h1>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={fetchData}>
            <FiRefreshCw /> Odswiez
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Matryca uprawnien</h3>
        </div>
        <div className="card-body" style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ minWidth: '180px', position: 'sticky', left: 0, background: 'inherit', zIndex: 1 }}>
                  Uzytkownik
                </th>
                {MODULES.map((mod) => (
                  <th key={mod} style={{ minWidth: '140px', textAlign: 'center' }}>
                    {AppModuleLabels[mod]}
                  </th>
                ))}
                <th style={{ minWidth: '100px', textAlign: 'center' }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const dirty = isRowDirty(user.id);
                const saving = savingUserId === user.id;
                return (
                  <tr key={user.id}>
                    <td
                      style={{
                        position: 'sticky',
                        left: 0,
                        background: 'inherit',
                        zIndex: 1,
                        fontWeight: 500,
                      }}
                    >
                      <div>{user.displayName}</div>
                      <div style={{ fontSize: '0.8em', opacity: 0.7 }}>{user.email}</div>
                    </td>
                    {MODULES.map((mod) => {
                      const currentLevel = permissions[user.id]?.[mod] || 'None';
                      return (
                        <td key={mod} style={{ textAlign: 'center' }}>
                          <Select
                            options={PERMISSION_LEVELS.map((level) => ({ value: level, label: PermissionLevelLabels[level] }))}
                            value={currentLevel}
                            onChange={(value) => handlePermissionChange(user.id, mod, value)}
                            size="sm"
                          />
                        </td>
                      );
                    })}
                    <td style={{ textAlign: 'center' }}>
                      {dirty && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleSave(user.id)}
                          disabled={saving}
                        >
                          {saving ? (
                            '...'
                          ) : (
                            <>
                              <FiSave /> Zapisz
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {users.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.6 }}>
              Brak uzytkownikow
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
